import { useKeycloak } from '@react-keycloak/web';
import { FC, MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Menu, MenuItem } from '@mui/material';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { TaskResponseExtended } from '@/api/data-contracts';
import { ReactComponent as TimeIcon } from '@/assets/icons/AccessTimeFilledRounded.svg';
import { ReactComponent as StatusIcon } from '@/assets/icons/status.svg';
import { orderKeys } from '@/features/orders';
import useOrdersService from '@/features/orders/api/orders.service';
import {
  CommentHeader,
  Container,
  ContentFooter,
  ContentSection,
  FooterButton,
  FooterButtonText,
  HeaderSection,
  MenuItemDone,
  MenuItemRejected,
  TimeAgo,
  UserName,
} from '@/features/orders/ui/Tasks/Task.styles';
import { queryKeys } from '@/react-query/constants';
import Avatar from '@/shared/ui/Avatar';

interface Props {
  task: TaskResponseExtended;
  orderId: string;
}

const KeyVisualsTask: FC<Props> = ({
  task: {
    id,
    resourceId,
    author: { userName },
    created,
    title,
    organizationId,
  },
  orderId,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const queryClient = useQueryClient();
  const { keycloak } = useKeycloak();
  const { updateStatusForOrderKeyVisuals } = useOrdersService();

  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const [currentUserOrganization, setCurrentUserOrganization] = useState<string>();

  useEffect(() => {
    keycloak.loadUserInfo().then((u: any) => setCurrentUserOrganization(u.organization));
  }, [keycloak]);

  const { mutate: updateStatus } = useMutation(
    (status: 'COMPLETED' | 'REJECTED') =>
      updateStatusForOrderKeyVisuals(params!.id!, id, { status }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([queryKeys.tasks, 'attachment', resourceId]);
        await queryClient.invalidateQueries(orderKeys.detail(orderId));
        await queryClient.invalidateQueries([queryKeys.keyVisuals, 'status']);
      },
    },
  );

  return (
    <Container>
      <CommentHeader>
        <HeaderSection>
          <Avatar name={userName} />
          <UserName>{userName}</UserName>
        </HeaderSection>

        <HeaderSection>
          <TimeIcon />
          <TimeAgo>{dayjs(created).fromNow()}</TimeAgo>
        </HeaderSection>
      </CommentHeader>

      <ContentSection>{t(title)}</ContentSection>

      <ContentFooter>
        {currentUserOrganization === organizationId && (
          <FooterButton
            isRight
            onClick={(event: MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget)}
          >
            <StatusIcon />
            <FooterButtonText>{t('Status')}</FooterButtonText>
          </FooterButton>
        )}

        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => updateStatus('COMPLETED')}>
            <MenuItemDone>•&nbsp;{t('Completed')}</MenuItemDone>
          </MenuItem>

          <MenuItem onClick={() => updateStatus('REJECTED')}>
            <MenuItemRejected>•&nbsp;{t('Rejected')}</MenuItemRejected>
          </MenuItem>
        </Menu>
      </ContentFooter>
    </Container>
  );
};

export default KeyVisualsTask;
